import React from 'react';
import { motion } from 'framer-motion';
import { FiPackage, FiTruck, FiShield, FiClock, FiTrendingUp, FiGlobe, FiUsers, FiCheckCircle } from 'react-icons/fi';
import './FulfillmentCenter.css';

const FulfillmentCenter = () => {
  const services = [
    {
      icon: FiPackage,
      title: 'Order Processing',
      description: 'Efficient order processing and fulfillment with real-time inventory management and automated picking and packing systems.',
    },
    {
      icon: FiTruck,
      title: 'Warehousing Solutions',
      description: 'State-of-the-art warehousing facilities with climate control, security systems, and optimized storage solutions.',
    },
    {
      icon: FiShield,
      title: 'Inventory Management',
      description: 'Advanced inventory tracking and management systems to ensure accuracy and optimize stock levels.',
    },
    {
      icon: FiClock,
      title: 'Fast Fulfillment',
      description: 'Same-day and next-day fulfillment options to meet your customers\' expectations and delivery deadlines.',
    },
    {
      icon: FiTrendingUp,
      title: 'Scalable Operations',
      description: 'Flexible fulfillment solutions that grow with your business, from startups to enterprise-level operations.',
    },
    {
      icon: FiGlobe,
      title: 'Global Distribution',
      description: 'Worldwide fulfillment network enabling you to reach customers across multiple countries and regions.',
    },
  ];

  const benefits = [
    {
      icon: FiCheckCircle,
      title: 'Reduced Costs',
      description: 'Eliminate the need for your own warehouse infrastructure and reduce operational expenses.',
    },
    {
      icon: FiCheckCircle,
      title: 'Improved Efficiency',
      description: 'Streamlined processes and automation reduce errors and increase fulfillment speed.',
    },
    {
      icon: FiCheckCircle,
      title: 'Focus on Growth',
      description: 'Free up resources to focus on product development and business growth.',
    },
    {
      icon: FiCheckCircle,
      title: 'Expert Support',
      description: 'Dedicated team of fulfillment experts to ensure your operations run smoothly.',
    },
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Integration',
      description: 'Seamlessly integrate your e-commerce platform or order management system with our fulfillment network.',
    },
    {
      number: '02',
      title: 'Inventory Storage',
      description: 'Send your products to our fulfillment centers where they\'ll be stored securely and managed efficiently.',
    },
    {
      number: '03',
      title: 'Order Processing',
      description: 'When orders come in, our team picks, packs, and prepares your products for shipment automatically.',
    },
    {
      number: '04',
      title: 'Shipping & Delivery',
      description: 'We handle shipping logistics and ensure your products reach customers quickly and safely.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <div className="fulfillment-center page-transition">
      {/* Hero Section */}
      <section className="fulfillment-hero">
        <div className="container">
          <motion.div
            className="fulfillment-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="fulfillment-hero-title">Fulfillment Center</h1>
            <p className="fulfillment-hero-subtitle">
              Comprehensive fulfillment solutions to streamline your e-commerce operations
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="fulfillment-intro">
        <div className="container">
          <motion.div
            className="fulfillment-intro-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="fulfillment-intro-text">
              <h2 className="section-title">Complete Fulfillment Solutions</h2>
              <p className="fulfillment-intro-description">
                Our state-of-the-art fulfillment centers are designed to handle your entire order
                fulfillment process from inventory storage to last-mile delivery. With advanced
                technology, experienced teams, and strategic locations, we help businesses of all
                sizes scale their operations efficiently.
              </p>
              <p className="fulfillment-intro-description">
                Whether you're a growing startup or an established enterprise, our fulfillment
                services provide the flexibility, scalability, and reliability you need to meet
                customer expectations and drive business growth.
              </p>
            </div>
            <div className="fulfillment-intro-stats">
              <div className="fulfillment-stat">
                <FiUsers size={40} />
                <div className="fulfillment-stat-content">
                  <h3>1000+</h3>
                  <p>Clients Served</p>
                </div>
              </div>
              <div className="fulfillment-stat">
                <FiPackage size={40} />
                <div className="fulfillment-stat-content">
                  <h3>10M+</h3>
                  <p>Orders Fulfilled</p>
                </div>
              </div>
              <div className="fulfillment-stat">
                <FiGlobe size={40} />
                <div className="fulfillment-stat-content">
                  <h3>50+</h3>
                  <p>Fulfillment Centers</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="fulfillment-services">
        <div className="container">
          <motion.div
            className="services-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Our Fulfillment Services</h2>
            <p className="section-description">
              Comprehensive solutions tailored to your business needs
            </p>
          </motion.div>

          <motion.div
            className="fulfillment-services-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="fulfillment-service-card"
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="fulfillment-service-icon">
                  <service.icon size={40} />
                </div>
                <h3 className="fulfillment-service-title">{service.title}</h3>
                <p className="fulfillment-service-description">{service.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="fulfillment-benefits">
        <div className="container">
          <motion.div
            className="benefits-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Why Choose Our Fulfillment Centers</h2>
            <p className="section-description">
              Partner with us to transform your fulfillment operations
            </p>
          </motion.div>

          <motion.div
            className="benefits-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="benefit-card"
                variants={itemVariants}
                whileHover={{ x: 10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="benefit-icon">
                  <benefit.icon size={32} />
                </div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="fulfillment-process">
        <div className="container">
          <motion.div
            className="process-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">How It Works</h2>
            <p className="section-description">
              Simple steps to get started with our fulfillment services
            </p>
          </motion.div>

          <motion.div
            className="process-steps"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                className="process-step-card"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <div className="process-step-number">{step.number}</div>
                <h3 className="process-step-title">{step.title}</h3>
                <p className="process-step-description">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="fulfillment-cta">
        <div className="container">
          <motion.div
            className="fulfillment-cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="fulfillment-cta-title">Ready to Optimize Your <span>Fulfillment</span>?</h2>
            <p className="fulfillment-cta-description">
              Get in touch with our team to discuss how we can help streamline your operations
            </p>
            <a href="/contact" className="btn btn-primary btn-large">
              Contact Us Today
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FulfillmentCenter;

